import React, { useState, useEffect, useRef } from 'react';
import API_BASE from '../config';

const MangaReader = ({ bookId, token, onClose, totalPages = 50 }) => {
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);

  const backend = API_BASE;

  // canvas
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // zoom / pan
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const dragging = useRef(false);
  const lastTouch = useRef([]);
  const lastDistance = useRef(null);

  const startX = useRef(0);
  const endX = useRef(0);

  const lastTap = useRef(0);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const getPageUrl = (p) =>
    `${backend}/books/${bookId}/page/${p}?token=${token}`;

  // =========================
  // LOAD IMAGE
  // =========================
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = getPageUrl(page);

    img.onload = () => {
      imgRef.current = img;
      setLoaded(true);
    };

    img.onerror = () => {
      console.error('[IMAGE FAIL]', page);
    };
  }, [page]);

  // =========================
  // DRAW CANVAS
  // =========================
  useEffect(() => {
    if (!loaded || !imgRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imgRef.current;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const scaleFit = Math.min(
      canvas.width / img.width,
      canvas.height / img.height
    );

    const drawScale = scaleFit * scale;

    const w = img.width * drawScale;
    const h = img.height * drawScale;

    const x = (canvas.width - w) / 2 + position.x;
    const y = (canvas.height - h) / 2 + position.y;

    // smooth fade transition
    ctx.globalAlpha = 0;
    requestAnimationFrame(() => {
      ctx.globalAlpha = 1;

      ctx.drawImage(img, x, y, w, h);

      // OPTIONAL WATERMARK (email)
      if (user.email) {
        ctx.save();

        ctx.font = '28px Arial';
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // rotate canvas for diagonal watermark
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.PI / 6); // -30 degrees

        const text = user.email;

        // repeat across screen
        const spacingX = 300;
        const spacingY = 180;

        for (let x = -canvas.width; x < canvas.width; x += spacingX) {
          for (let y = -canvas.height; y < canvas.height; y += spacingY) {
            ctx.fillText(text, x, y);
          }
        }

        ctx.restore();
      }
    });
  }, [loaded, scale, position, page]);

  // =========================
  // PRELOAD NEXT PAGE
  // =========================
  useEffect(() => {
    if (page < totalPages) {
      const img = new Image();
      img.src = getPageUrl(page + 1);
    }
  }, [page]);

  // =========================
  // NAVIGATION
  // =========================
  const nextPage = () => {
    if (page < totalPages) {
      setLoaded(false);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setPage(p => p + 1);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setLoaded(false);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setPage(p => p - 1);
    }
  };

  // =========================
  // CLICK NAVIGATION
  // =========================
  const handleClick = (e) => {
    if (scale > 1) return;

    const x = e.clientX;
    const width = window.innerWidth;

    if (x < width / 2) prevPage();
    else nextPage();
  };

  // =========================
  // DOUBLE TAP ZOOM
  // =========================
  const handleTap = (e) => {
    const now = Date.now();

    if (now - lastTap.current < 300) {
      setScale(s => (s === 1 ? 2 : 1));
      setPosition({ x: 0, y: 0 });
    }

    lastTap.current = now;
  };

  // =========================
  // TOUCH HANDLING
  // =========================
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      startX.current = e.touches[0].clientX;
      dragging.current = true;
      lastTouch.current = [{ x: e.touches[0].clientX, y: e.touches[0].clientY }];
    }

    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastDistance.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      const t = e.touches[0];

      const dx = t.clientX - lastTouch.current[0].x;
      const dy = t.clientY - lastTouch.current[0].y;

      setPosition(p => ({ x: p.x + dx, y: p.y + dy }));

      lastTouch.current = [{ x: t.clientX, y: t.clientY }];
    }

    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (lastDistance.current) {
        const diff = dist - lastDistance.current;
        setScale(s => Math.min(Math.max(s + diff * 0.005, 1), 4));
      }

      lastDistance.current = dist;
    }
  };

  const handleTouchEnd = (e) => {
    dragging.current = false;

    if (scale === 1 && e.changedTouches.length === 1) {
      endX.current = e.changedTouches[0].clientX;
      const diff = startX.current - endX.current;

      if (diff > 50) nextPage();
      if (diff < -50) prevPage();
    }

    lastDistance.current = null;
  };

  // =========================
  // KEYBOARD
  // =========================
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextPage();
      if (e.key === 'ArrowLeft') prevPage();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [page]);

  // =========================
  // BLOCK CONTEXT MENU + SELECTION
  // =========================
  const blockContext = (e) => e.preventDefault();

  return (
    <div
      className="fixed inset-0 bg-black z-50 flex flex-col select-none"
      onContextMenu={blockContext}
      style={{ WebkitTouchCallout: 'none' }}
    >
      {/* TOP BAR */}
      <div className="flex justify-between items-center p-3 text-white bg-black/70 backdrop-blur">
        <span className="text-sm">
          Page {page} / {totalPages}
        </span>

        <button onClick={onClose} className="text-xl">✕</button>
      </div>

      {/* CANVAS VIEWER */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden"
        onClick={handleClick}
        onTouchStart={(e) => {
          handleTouchStart(e);
          handleTap(e);
        }}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!loaded && (
          <div className="absolute text-white animate-pulse">
            Loading...
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="max-w-full max-h-full transition-opacity duration-300"
          style={{
            opacity: loaded ? 1 : 0,
            touchAction: 'none'
          }}
        />
      </div>

      {/* CONTROLS */}
      <div className="flex justify-between p-3 bg-black/70 text-white text-sm">
        <button onClick={prevPage} disabled={page === 1}>◀ Prev</button>
        <button onClick={nextPage} disabled={page === totalPages}>Next ▶</button>
      </div>
    </div>
  );
};

export default MangaReader;